import api from "./api";
import { generateErrorMessage } from "../utils/message";

/* 📌 Register a New Material */
export const registerMaterial = async (materialData) => {
  try {
    const response = await api.post("/materials", materialData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("register", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Fetch List of Materials */
export const fetchMaterialsFromAPI = async (page = 1, perPage = 10, search = "", columnFilters = {}, sort = {}) => {
  try {
    // Build params object with column-specific filters
    const params = {
      page,
      per_page: perPage
    };

    // Add search parameter if provided (for backward compatibility)
    if (search) {
      params.search = search;
    }

    // Add column-specific parameters
    if (columnFilters.vendor_code) params.vendor_code = columnFilters.vendor_code;
    if (columnFilters.matl_code) params.matl_code = columnFilters.matl_code;
    if (columnFilters.item_no) params.item_no = columnFilters.item_no;
    if (columnFilters.sebango) params.sebango = columnFilters.sebango;
    if (columnFilters.part_name) params.part_name = columnFilters.part_name;
    if (columnFilters.project) params.project = columnFilters.project;

    // Server-side sort
    if (sort && sort.sortBy) {
      params.sort_by = sort.sortBy;
      params.sort_dir = sort.sortDir === 'desc' ? 'desc' : 'asc';
    }

    const response = await api.get("/materials", {
      params: params,
    });

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Fetch every material matching the current filters (for Export) */
// The list endpoint is server-side paginated and caps per_page at 1000, so a
// single request can never return the whole result set — this walks the pages.
export const fetchAllMaterialsForExport = async (
  { search = "", columnFilters = {}, sort = {} } = {},
  onProgress,
) => {
  const PER_PAGE = 1000;
  const collected = [];
  let page = 1;
  let total = 0;

  for (;;) {
    const response = await fetchMaterialsFromAPI(page, PER_PAGE, search, columnFilters, sort);
    const payload = response?.materials || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];

    collected.push(...rows);

    const reportedTotal = Number(payload.total);
    const hasTotal = Number.isFinite(reportedTotal) && reportedTotal > 0;
    total = hasTotal ? reportedTotal : collected.length;

    if (onProgress) {
      onProgress(collected.length, total);
    }

    const reportedLastPage = Number(payload.last_page);
    const hasLastPage = Number.isFinite(reportedLastPage) && reportedLastPage > 0;

    // A short page is the reliable end-of-data signal, so the export still
    // completes if the payload ever omits `total` or `last_page`. Stopping on a
    // missing field instead would silently truncate the file — the exact bug
    // this function exists to fix.
    if (rows.length === 0) break;
    if (hasLastPage && page >= reportedLastPage) break;
    if (hasTotal && collected.length >= reportedTotal) break;
    if (rows.length < PER_PAGE) break;

    page += 1;
  }

  return collected;
};

/* 📌 Fetch Material by Vendor Code & Material Code */
export const fetchMaterialsById = async (vendorCode, matlCode) => {
  try {
    const response = await api.get(`/materials/${vendorCode}/${matlCode}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data material", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Update Material by Vendor Code & Material Code */
export const updateMaterial = async (vendorCode,materialData) => {
  try {
    const response = await api.put(`/materials`, materialData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("update", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Delete Material by Vendor Code & Material Code */
export const deleteMaterialsFromAPI = async (vendorCode, matlCode) => {
  try {
    const response = await api.delete(`/materials/${vendorCode}/${matlCode}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("delete data", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Upload Material Excel File */
export const uploadMaterialFileToAPI = async (formData, onUploadProgress) => {
  try {
    const response = await api.post("/materials/submit-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) {
              onUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          }
        : undefined,
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("upload material file", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Sync Materials from External API */
export const syncMaterialsFromAPI = async (activeOnly = false, keepExisting = false) => {
  try {
    const params = new URLSearchParams({
      active_only: activeOnly.toString(),
      keep_existing: keepExisting.toString()
    });
    
    const response = await api.post(`/sync/materials?${params}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("sync", "material");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Bulk Update Materials by Vendor Code */
export const bulkUpdateMaterialsByVendorCode = async (vendor_code, fg, wip) => {
  try {
    const normalizedFg = typeof fg === 'string' ? Number(fg.replace(/\./g, '').replace(',', '.')) : Number(fg || 0);
    const normalizedWip = typeof wip === 'string' ? Number(wip.replace(/\./g, '').replace(',', '.')) : Number(wip || 0);

    const response = await api.put("/materials/bulk-update-by-vendor", {
      vendor_code,
      fg: Number.isNaN(normalizedFg) ? 0 : normalizedFg,
      wip: Number.isNaN(normalizedWip) ? 0 : normalizedWip
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("bulk update", "materials");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};
